/**
 * API client for communicating with the backend.
 *
 * Features:
 * - Automatic cookie-based authentication (credentials: "include")
 * - Idempotency key support for safe retries
 * - Structured error handling with ApiError
 * - Request ID extraction from body or X-Request-ID header
 */

import { handleRuntimeReleaseHeaders } from '@/lib/runtime-release-guard';
import { CLIENT_CONTEXT_HEADER, getClientContextHeader } from '@/telemetry/activity/client-context';
import {
  CLIENT_ACTIVITY_HEADER,
  getClientActivityHeader,
  noteActivityAction,
  isActivityReadOnlyPost,
} from '@/telemetry/activity/client-hooks';
import { captureApiErrorTelemetry } from '@/telemetry/errorSink';
import type { HttpMethod, MutatingMethod, RequestOptions } from '@/types/api';

import { ApiError } from './errors';
import { RequestAdmissionGate } from './request-admission';
import {
  __resetAuthSessionBarrierForTests,
  assertAuthSessionAvailable,
  assertSessionAuthFailureCurrent,
  assertResponseBelongsToAuthSession,
  captureAuthSessionSnapshot,
  getSessionAuthFailure,
  recordAuthTransitionStarted,
  publishAuthTransitionInProgress,
  recordCurrentSessionRecovery,
  recordLoginSuccess,
  recordLogoutSuccess,
  registerSessionAuthFailure,
  runWithSharedAuthSessionLock,
  type AuthSessionSnapshot,
} from './session-auth-barrier';
import {
  getRequestPathname,
  isReadWebRequest,
  pathMatchesPrefix,
  READ_WEB_LOCAL_MAX_INFLIGHT_REQUESTS,
  READ_WEB_PERMIT_MAX_HOLD_MS,
} from './web-runtime-policy';

/**
 * Base URL paths for different API namespaces.
 */
const API_BASES = {
  webAdmin: '/api/web-admin',
  erp: '/api/erp',
} as const;

/**
 * Methods that may need idempotency keys.
 * DELETE is included because some endpoints (like document link deletion) require idempotency.
 */
const MUTATING_METHODS: Set<MutatingMethod> = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
const RETRYABLE_STATUS_CODES = new Set([502, 503, 504]);
const TRANSIENT_RETRY_MAX_ATTEMPTS = 1;
const TENANT_RUNTIME_BUSY_RETRY_MAX_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 250;
const RETRY_MAX_DELAY_MS = 2_000;
const TENANT_RUNTIME_BUSY_RETRY_BASE_DELAY_MS = 700;
const TENANT_RUNTIME_BUSY_RETRY_MAX_DELAY_MS = 5_000;
let tenantRuntimeBusyCooldownUntilMs = 0;
let readWebRequestGate = new RequestAdmissionGate(READ_WEB_LOCAL_MAX_INFLIGHT_REQUESTS);
let authEndpointRequestGate = new RequestAdmissionGate(1);
let pendingAuthMutationIntents = 0;

function isLoginRequest(url: string): boolean {
  return getRequestPathname(url) === '/api/web-admin/auth/login';
}

function isCurrentSessionRequest(url: string): boolean {
  return getRequestPathname(url) === '/api/web-admin/auth/me';
}

function isLogoutRequest(url: string): boolean {
  return getRequestPathname(url) === '/api/web-admin/auth/logout';
}

function isAuthEndpoint(url: string): boolean {
  return pathMatchesPrefix(getRequestPathname(url), '/api/web-admin/auth');
}

async function runAdmittedFetch<T>(
  url: string,
  method: HttpMethod,
  requestInit: RequestInit,
  bindAuthSnapshot: (requestAuthSnapshot: AuthSessionSnapshot) => void,
  consume: (response: Response, requestAuthSnapshot: AuthSessionSnapshot) => Promise<T>,
): Promise<T> {
  if (!isReadWebRequest(url, method)) {
    const requestAuthSnapshot = captureAuthSessionSnapshot();
    bindAuthSnapshot(requestAuthSnapshot);
    return consume(await fetch(url, requestInit), requestAuthSnapshot);
  }
  const gate = readWebRequestGate;
  const release = await gate.acquire(requestInit.signal);
  let overdueRelease: ReturnType<typeof setTimeout> | undefined;
  // A cookieless request is outside the session barrier (see executeRequestWithDecoder).
  const sessionBound = !isAuthEndpoint(url) && requestInit.credentials !== 'omit';
  try {
    if (sessionBound) {
      assertAuthSessionAvailable();
    }
    // A cooldown may have been installed while this request was queued.
    await waitForReadWebCooldown(requestInit.signal);
    if (sessionBound) {
      assertAuthSessionAvailable();
    }
    // Snapshot immediately before the network attempt. A request may have
    // waited in this queue across a successful login and then belongs to the
    // new session, not the session that originally created fetchJson().
    const requestAuthSnapshot = captureAuthSessionSnapshot();
    bindAuthSnapshot(requestAuthSnapshot);
    // A stalled connection leaves a read unanswered for a minute or more. It is
    // not cut off, but it stops holding back the reads queued behind it.
    overdueRelease = setTimeout(() => {
      // A gate that a test reset replaced sends nothing into the next test.
      if (gate === readWebRequestGate) release();
    }, READ_WEB_PERMIT_MAX_HOLD_MS);
    return await consume(await fetch(url, requestInit), requestAuthSnapshot);
  } finally {
    clearTimeout(overdueRelease);
    release();
  }
}

export function __resetApiClientRuntimeForTests(): void {
  tenantRuntimeBusyCooldownUntilMs = 0;
  __resetAuthSessionBarrierForTests();
  readWebRequestGate = new RequestAdmissionGate(READ_WEB_LOCAL_MAX_INFLIGHT_REQUESTS);
  authEndpointRequestGate = new RequestAdmissionGate(1);
  pendingAuthMutationIntents = 0;
}

async function runAuthEndpointExclusive<T>(
  operation: () => Promise<T>,
  signal?: AbortSignal | null,
): Promise<T> {
  const releaseLocal = await authEndpointRequestGate.acquire(signal);
  try {
    return await runWithSharedAuthSessionLock(operation, signal);
  } finally {
    releaseLocal();
  }
}

function isAbortError(error: unknown): boolean {
  if (error instanceof DOMException) {
    return error.name === 'AbortError';
  }
  if (error instanceof Error) {
    return error.name === 'AbortError';
  }
  return false;
}

function computeRetryDelayMs(attempt: number, retryAfterMs: number | null): number {
  if (retryAfterMs !== null) {
    return retryAfterMs;
  }
  const expDelay = Math.min(RETRY_MAX_DELAY_MS, RETRY_BASE_DELAY_MS * 2 ** attempt);
  const jitter = Math.floor(Math.random() * 100);
  return expDelay + jitter;
}

function computeTenantRuntimeBusyDelayMs(attempt: number, retryAfterMs: number | null): number {
  if (retryAfterMs !== null) {
    return retryAfterMs;
  }
  const expDelay = Math.min(
    TENANT_RUNTIME_BUSY_RETRY_MAX_DELAY_MS,
    TENANT_RUNTIME_BUSY_RETRY_BASE_DELAY_MS * 2 ** attempt,
  );
  const jitter = Math.floor(Math.random() * 250);
  return expDelay + jitter;
}

function canRetryTransientFailure(method: HttpMethod, hasIdempotencyKey: boolean): boolean {
  if (method === 'GET') {
    return true;
  }
  return MUTATING_METHODS.has(method as MutatingMethod) && hasIdempotencyKey;
}

function shouldRetryApiError(
  error: ApiError,
  options: {
    method: HttpMethod;
    hasIdempotencyKey: boolean;
    attempt: number;
    signal: AbortSignal | null | undefined;
  },
): boolean {
  const { method, hasIdempotencyKey, attempt, signal } = options;
  if (!canRetryTransientFailure(method, hasIdempotencyKey)) {
    return false;
  }
  if (signal?.aborted) {
    return false;
  }
  if (error.isTenantRuntimeBusy()) {
    return attempt < TENANT_RUNTIME_BUSY_RETRY_MAX_ATTEMPTS;
  }
  if (attempt >= TRANSIENT_RETRY_MAX_ATTEMPTS) {
    return false;
  }
  return RETRYABLE_STATUS_CODES.has(error.statusCode);
}

function shouldRetryNetworkFailure(
  error: unknown,
  options: {
    method: HttpMethod;
    hasIdempotencyKey: boolean;
    attempt: number;
    signal: AbortSignal | null | undefined;
  },
): boolean {
  const { method, hasIdempotencyKey, attempt, signal } = options;
  if (attempt >= TRANSIENT_RETRY_MAX_ATTEMPTS) {
    return false;
  }
  if (!canRetryTransientFailure(method, hasIdempotencyKey)) {
    return false;
  }
  if (signal?.aborted || isAbortError(error)) {
    return false;
  }
  return true;
}

function sleep(ms: number, signal?: AbortSignal | null): Promise<void> {
  if (ms <= 0) {
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      cleanup();
      resolve();
    }, ms);
    const handleAbort = () => {
      cleanup();
      reject(new DOMException('Aborted', 'AbortError'));
    };
    const cleanup = () => {
      clearTimeout(timeoutId);
      signal?.removeEventListener('abort', handleAbort);
    };
    if (signal) {
      if (signal.aborted) {
        cleanup();
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }
      signal.addEventListener('abort', handleAbort, { once: true });
    }
  });
}

function markTenantRuntimeBusyCooldown(delayMs: number): void {
  tenantRuntimeBusyCooldownUntilMs = Math.max(
    tenantRuntimeBusyCooldownUntilMs,
    Date.now() + delayMs,
  );
}

async function waitForReadWebCooldown(signal?: AbortSignal | null): Promise<void> {
  const remainingMs = tenantRuntimeBusyCooldownUntilMs - Date.now();
  if (remainingMs <= 0) {
    return;
  }
  await sleep(remainingMs, signal);
}

/**
 * Generate a UUID v4 with cryptographic randomness, including local HTTP on phones.
 */
export function generateIdempotencyKey(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Build the full URL with query parameters.
 */
function buildUrl(
  url: string,
  params?: Record<
    string,
    string | number | boolean | undefined | null | Array<string | number | boolean>
  >,
): string {
  if (!params) return url;

  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) {
      continue;
    }
    if (Array.isArray(value)) {
      for (const item of value) {
        searchParams.append(key, String(item));
      }
      continue;
    }
    searchParams.append(key, String(value));
  }

  const queryString = searchParams.toString();
  if (!queryString) return url;

  return url.includes('?') ? `${url}&${queryString}` : `${url}?${queryString}`;
}

type SuccessResponseDecoder<T> = (response: Response) => Promise<T>;

export interface ApiBlobResponse {
  blob: Blob;
  headers: Headers;
}

async function parseJsonBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get('Content-Type') ?? '';
  if (!contentType.includes('application/json')) {
    return null;
  }
  try {
    return await response.json();
  } catch {
    return null;
  }
}

async function executeRequestWithDecoder<T>(
  url: string,
  options: RequestOptions,
  decodeSuccess: SuccessResponseDecoder<T>,
  sharedAuthSessionLockHeld = false,
): Promise<T> {
  const {
    idempotencyKey,
    autoIdempotencyKey = true,
    skipAuthRedirect = false,
    transientRetry = true,
    body,
    params,
    headers: customHeaders,
    method = 'GET',
    ...fetchOptions
  } = options;

  // Build headers
  const headers = new Headers(customHeaders);

  // Always include credentials for cookie auth
  const credentials = options.credentials ?? 'include';

  const isFormDataBody = typeof FormData !== 'undefined' && body instanceof FormData;

  // Set Content-Type for JSON body. Browser must set multipart boundaries for FormData.
  if (body !== undefined && !isFormDataBody) {
    headers.set('Content-Type', 'application/json');
  }

  // Handle idempotency key for mutating requests
  const upperMethod = method.toUpperCase() as HttpMethod;
  if (MUTATING_METHODS.has(upperMethod as MutatingMethod)) {
    const key = idempotencyKey ?? (autoIdempotencyKey ? generateIdempotencyKey() : undefined);
    if (key) {
      headers.set('Idempotency-Key', key);
    }
    if (upperMethod !== 'POST' || !isActivityReadOnlyPost(url)) noteActivityAction();
  }
  // Ties this request to the tab's screen and idle time (MD-0099); ids only. The attempt
  // number (`try`) marks the transport's own retries, which are not user actions.
  const activityHeader = getClientActivityHeader();
  const clientContext = getClientContextHeader(url);
  if (clientContext) headers.set(CLIENT_CONTEXT_HEADER, clientContext);
  const hasIdempotencyKey = Boolean(headers.get('Idempotency-Key'));

  // Build final URL with params
  const finalUrl = buildUrl(url, params);
  const readWebRequest = isReadWebRequest(finalUrl, upperMethod);
  // A request that sends no cookie is not part of the cookie session (the owner report
  // Mini App carries its own bearer token, MD-0128): the session barrier neither blocks
  // nor binds it, and its 401 never installs one.
  const outsideCookieSession = credentials === 'omit';
  const responseMustStayInSession =
    !outsideCookieSession && (!isAuthEndpoint(finalUrl) || isCurrentSessionRequest(finalUrl));
  const requestAuthBinding: { snapshot: AuthSessionSnapshot | null } = { snapshot: null };

  const bindRequestAuthSnapshot = (requestAuthSnapshot: AuthSessionSnapshot): void => {
    if (!responseMustStayInSession) return;
    if (requestAuthBinding.snapshot === null) {
      requestAuthBinding.snapshot = requestAuthSnapshot;
      return;
    }
    // A logical request, including all retries, belongs to the exact cookie
    // session used by its first network attempt.
    assertResponseBelongsToAuthSession(requestAuthBinding.snapshot);
  };

  const encodedBody = body === undefined ? undefined : isFormDataBody ? body : JSON.stringify(body);
  const sharedRequestInit: RequestInit = {
    ...fetchOptions,
    method: upperMethod,
    headers,
    credentials,
  };

  let attempt = 0;
  for (;;) {
    if (!isAuthEndpoint(finalUrl) && !outsideCookieSession) {
      assertAuthSessionAvailable();
    }

    let result: { data: T | undefined; apiError: ApiError | null; retryDelayMs: number | null };
    try {
      const requestInit: RequestInit = {
        ...sharedRequestInit,
      };
      if (activityHeader) {
        const attemptHeaders = new Headers(headers);
        attemptHeaders.set(CLIENT_ACTIVITY_HEADER, `${activityHeader}; try=${attempt}`);
        requestInit.headers = attemptHeaders;
      }
      if (encodedBody !== undefined) {
        requestInit.body = encodedBody;
      }
      if (!isAuthEndpoint(finalUrl) && !outsideCookieSession) {
        assertAuthSessionAvailable();
      }
      result = await runAdmittedFetch(
        finalUrl,
        upperMethod,
        requestInit,
        bindRequestAuthSnapshot,
        async (response, requestAuthSnapshot) => {
          handleRuntimeReleaseHeaders(response.headers);

          if (responseMustStayInSession) {
            assertResponseBelongsToAuthSession(requestAuthSnapshot);
          }

          const errorBody = response.ok ? null : await parseJsonBody(response);
          if (responseMustStayInSession) {
            // Body decoding yields to the event loop; a sibling request may have
            // invalidated the session in the meantime.
            assertResponseBelongsToAuthSession(requestAuthSnapshot);
          }
          const apiError = response.ok ? null : ApiError.fromResponse(response, errorBody);
          if (apiError?.isSessionAuthError()) {
            if (!skipAuthRedirect && !outsideCookieSession) {
              // Establish the barrier while the read permit is still held, so
              // the next queued request observes it before touching the network.
              // An overdue read no longer holds one: reads sent meanwhile are
              // siblings of the old session and end as stale.
              const committedFailure = await registerSessionAuthFailure(
                apiError,
                requestAuthSnapshot,
                {
                  sharedLockHeld: sharedAuthSessionLockHeld,
                },
              );
              if (committedFailure) {
                // Web Locks may schedule another cookie boundary before the
                // request() promise resumes. Only the failure that still owns
                // the active barrier may authorize returning this current 401.
                assertSessionAuthFailureCurrent(committedFailure);
              } else {
                // A login/logout committed while this 401 waited for the
                // cross-tab lock. Never expose the old session's response.
                assertResponseBelongsToAuthSession(requestAuthSnapshot);
              }
            }
          }
          let retryDelayMs: number | null = null;
          let tenantRuntimeBusyDelayMs: number | null = null;
          if (apiError?.isTenantRuntimeBusy()) {
            tenantRuntimeBusyDelayMs = computeTenantRuntimeBusyDelayMs(
              attempt,
              apiError.retryAfterMs ?? null,
            );
            if (readWebRequest) {
              // Publish overload evidence even after this request exhausts its
              // own retry budget; newly queued reads still need the cooldown.
              markTenantRuntimeBusyCooldown(tenantRuntimeBusyDelayMs);
            }
          }
          if (
            apiError &&
            (transientRetry || apiError.isTenantRuntimeBusy()) &&
            shouldRetryApiError(apiError, {
              method: upperMethod,
              hasIdempotencyKey,
              attempt,
              signal: fetchOptions.signal,
            })
          ) {
            retryDelayMs = apiError.isTenantRuntimeBusy()
              ? tenantRuntimeBusyDelayMs
              : computeRetryDelayMs(attempt, apiError.retryAfterMs ?? null);
          }
          const data = response.ok ? await decodeSuccess(response) : undefined;
          if (response.ok && responseMustStayInSession) {
            assertResponseBelongsToAuthSession(requestAuthSnapshot);
          }
          return { data, apiError, retryDelayMs };
        },
      );
    } catch (error) {
      if (error === getSessionAuthFailure()) {
        throw error;
      }
      if (isAbortError(error)) {
        throw error;
      }
      if (requestAuthBinding.snapshot) {
        // fetch() can reject without producing a Response. A terminal network
        // failure still belongs to the session used by the first attempt and
        // must not become a retryable network_error under a newer principal.
        assertResponseBelongsToAuthSession(requestAuthBinding.snapshot);
      }
      if (error instanceof ApiError) {
        throw error;
      }
      if (
        transientRetry &&
        shouldRetryNetworkFailure(error, {
          method: upperMethod,
          hasIdempotencyKey,
          attempt,
          signal: fetchOptions.signal,
        })
      ) {
        const delayMs = computeRetryDelayMs(attempt, null);
        attempt += 1;
        await sleep(delayMs, fetchOptions.signal);
        continue;
      }
      throw ApiError.networkError(error instanceof Error ? error : undefined);
    }

    const { data, apiError, retryDelayMs } = result;
    if (apiError) {
      if (retryDelayMs !== null) {
        attempt += 1;
        await sleep(retryDelayMs, fetchOptions.signal);
        continue;
      }
      try {
        captureApiErrorTelemetry(apiError, {
          route: window.location.pathname,
          pageUrl: window.location.href,
          component: 'api-client',
        });
      } catch {
        // Telemetry must never block API error propagation.
      }
      throw apiError;
    }

    if (isCurrentSessionRequest(finalUrl) && requestAuthBinding.snapshot) {
      // `/auth/me` is a recovery probe only when it started behind an existing
      // barrier. A routine probe must not make concurrent, legitimate 401s stale.
      recordCurrentSessionRecovery(requestAuthBinding.snapshot);
    }
    return data as T;
  }
}

async function requestWithDecoder<T>(
  url: string,
  options: RequestOptions,
  decodeSuccess: SuccessResponseDecoder<T>,
): Promise<T> {
  if (!isAuthEndpoint(url)) {
    return executeRequestWithDecoder(url, options, decodeSuccess);
  }

  const authMutationKind = isLoginRequest(url) ? 'login' : isLogoutRequest(url) ? 'logout' : null;
  if (!authMutationKind) {
    return runAuthEndpointExclusive(
      () => executeRequestWithDecoder(url, options, decodeSuccess, true),
      options.signal,
    );
  }

  // Declare every login/logout intent before waiting for the FIFO lane. This
  // immediately stales protected work and keeps the app fail-closed while a
  // response may already have changed the HttpOnly cookie.
  pendingAuthMutationIntents += 1;
  recordAuthTransitionStarted();
  let settled = false;
  const settleAuthMutation = (succeeded: boolean): void => {
    if (settled) return;
    settled = true;
    pendingAuthMutationIntents -= 1;
    if (pendingAuthMutationIntents === 0) {
      if (succeeded && authMutationKind === 'login') {
        recordLoginSuccess();
      } else {
        // Logout success and every ambiguous/failed final auth mutation are
        // fail-closed. A later explicit /auth/me probe may reconcile a cookie
        // that the server committed before a network failure.
        recordLogoutSuccess();
      }
      tenantRuntimeBusyCooldownUntilMs = 0;
    }
  };

  try {
    return await runAuthEndpointExclusive(async () => {
      // Another tab may have completed an earlier auth operation while this
      // intent waited for the Web Lock. Close every peer again before fetch.
      publishAuthTransitionInProgress();
      try {
        const data = await executeRequestWithDecoder(url, options, decodeSuccess, true);
        // Commit the barrier before releasing the auth lane. Otherwise a
        // queued /auth/me could start against an uncommitted cookie boundary.
        settleAuthMutation(true);
        return data;
      } catch (error) {
        settleAuthMutation(false);
        throw error;
      }
    }, options.signal);
  } catch (error) {
    // Also settle an operation aborted before it acquired the FIFO/Web Lock.
    settleAuthMutation(false);
    throw error;
  }
}

/**
 * Main JSON request wrapper with retries, auth lifecycle handling, and runtime
 * admission. FormData bodies are passed through without a Content-Type header.
 */
export function fetchJson<T>(url: string, options: RequestOptions = {}): Promise<T> {
  return requestWithDecoder<T>(url, options, async (response) => {
    return (await parseJsonBody(response)) as T;
  });
}

/**
 * Fetch a same-origin API response as text through the shared transport.
 */
export function fetchText(url: string, options: RequestOptions = {}): Promise<string> {
  return requestWithDecoder(url, options, (response) => response.text());
}

/**
 * Fetch a same-origin API response as a Blob while retaining response headers.
 */
export function fetchBlob(url: string, options: RequestOptions = {}): Promise<ApiBlobResponse> {
  return requestWithDecoder(url, options, async (response) => ({
    blob: await response.blob(),
    headers: response.headers,
  }));
}

/**
 * HTTP GET request.
 */
export function get<T>(url: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
  return fetchJson<T>(url, { ...options, method: 'GET' });
}

/**
 * HTTP POST request.
 */
export function post<T>(
  url: string,
  body?: unknown,
  options?: Omit<RequestOptions, 'method' | 'body'>,
): Promise<T> {
  return fetchJson<T>(url, { ...options, method: 'POST', body });
}

/**
 * HTTP PUT request.
 */
export function put<T>(
  url: string,
  body?: unknown,
  options?: Omit<RequestOptions, 'method' | 'body'>,
): Promise<T> {
  return fetchJson<T>(url, { ...options, method: 'PUT', body });
}

/**
 * HTTP PATCH request.
 */
export function patch<T>(
  url: string,
  body?: unknown,
  options?: Omit<RequestOptions, 'method' | 'body'>,
): Promise<T> {
  return fetchJson<T>(url, { ...options, method: 'PATCH', body });
}

/**
 * HTTP DELETE request.
 */
export function del<T>(url: string, options?: Omit<RequestOptions, 'method'>): Promise<T> {
  return fetchJson<T>(url, { ...options, method: 'DELETE' });
}

/**
 * API client object with namespaced methods for different API bases.
 */
export const api = {
  /**
   * Web Admin API (/api/web-admin/*)
   */
  webAdmin: {
    get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
      get<T>(`${API_BASES.webAdmin}${path}`, options),

    text: (path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
      fetchText(`${API_BASES.webAdmin}${path}`, { ...options, method: 'GET' }),

    blob: (path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
      fetchBlob(`${API_BASES.webAdmin}${path}`, { ...options, method: 'GET' }),

    post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
      post<T>(`${API_BASES.webAdmin}${path}`, body, options),

    put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
      put<T>(`${API_BASES.webAdmin}${path}`, body, options),

    patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
      patch<T>(`${API_BASES.webAdmin}${path}`, body, options),

    delete: <T>(path: string, options?: Omit<RequestOptions, 'method'>) =>
      del<T>(`${API_BASES.webAdmin}${path}`, options),
  },

  /**
   * ERP API (/api/erp/*)
   */
  erp: {
    get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
      get<T>(`${API_BASES.erp}${path}`, options),

    text: (path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
      fetchText(`${API_BASES.erp}${path}`, { ...options, method: 'GET' }),

    blob: (path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
      fetchBlob(`${API_BASES.erp}${path}`, { ...options, method: 'GET' }),

    post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
      post<T>(`${API_BASES.erp}${path}`, body, options),

    put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
      put<T>(`${API_BASES.erp}${path}`, body, options),

    patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
      patch<T>(`${API_BASES.erp}${path}`, body, options),

    delete: <T>(path: string, options?: Omit<RequestOptions, 'method'>) =>
      del<T>(`${API_BASES.erp}${path}`, options),
  },
};

// Export individual functions and types
export { ApiError } from './errors';
export type { RequestOptions, HttpMethod, MutatingMethod } from '@/types/api';
